import { SHContainer } from "@components/design-systems";
import {
  ESGPoliciesSVG,
  FinancialStrengthSVG,
  OperationalSVG,
} from "@components/svgs";
import { StepCompProps, StepCompRef } from "@layouts/stepper";
import { StepName } from "@layouts/stepper/step-name";
import { RootState } from "@redux/store";
import {
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useEffectOnce } from "react-use";
import { useReview } from "@hooks/useReview";
import { BusinessMetricCategories } from "@pages/platform-analysis/_id/steps/business-metric/selection/components/business-metric-categories";
import { CategoriesLabel } from "@pages/platform-analysis/_id/steps/business-metric/selection/components/categories-label";
import { BusinessMetricSelectionSkeleton } from "@pages/platform-analysis/_id/steps/business-metric/selection/skeleton";
import { updateBusinessMetricSelectionAction } from "@redux/slices/review";
import { chain, concat, filter, isEmpty } from "lodash";

export interface ReviewBusinessMetricSelectionProps extends StepCompProps {}
export const ReviewBusinessMetricSelection: ForwardRefRenderFunction<
  StepCompRef,
  StepCompProps
> = ({ stepNumber }, ref) => {
  const dispatch = useDispatch();
  const { reviewId } = useParams<{ reviewId: string }>();

  const {
    businessMetricSelections,
    ui: { isDirty, isLoading },
  } = useSelector((state: RootState) => state.review);
  const {
    setIsDirty,
    setIsValid,
    updateReviewBusinessMetricSelections,
    loadReviewBusinessMetricSelections,
  } = useReview();

  const loadSelections = async (reviewId?: string) => {
    if (!reviewId || reviewId === "new") return;
    const businessMetricSelections = await loadReviewBusinessMetricSelections({
      reviewId,
    });
    dispatch(updateBusinessMetricSelectionAction(businessMetricSelections));
    setIsDirty(businessMetricSelections?.hasRecalculate || false);
  };

  useImperativeHandle(ref, () => ({
    onChangeStep: async (newStep) => {
      setIsDirty(false);
      return await handleOnSubmit();
    },
    onChangeOtherStep: async () => {
      setIsDirty(false);
      return await handleOnSubmit(); 
    },
    onSubmit: async () => {
      setIsDirty(false);
      return await handleOnSubmit();
    },
  }));

  const handleOnSubmit = async () => {
    let isSuccess = true;
    if (!reviewId) return false;
    if (businessMetricSelections && isDirty) {
      isSuccess = await updateReviewBusinessMetricSelections({
        reviewId,
        businessMetricSelections,
      });
    }

    return isSuccess;
  };

  const hasSelectedBusinessMetric = useMemo(() => {
    return !isEmpty(
      filter(
        chain(
          concat(
            businessMetricSelections?.financialStrength || [],
            businessMetricSelections?.esg || [],
            businessMetricSelections?.operational || [],
          ),
        )?.value(),
        { isSelected: true },
      ),
    );
  }, [businessMetricSelections]);

  useEffect(() => {
    setIsValid(hasSelectedBusinessMetric);
    // eslint-disable-next-line
  }, [hasSelectedBusinessMetric]);

  useEffect(() => {
    loadSelections(reviewId);
    // eslint-disable-next-line
  }, [reviewId]);

  useEffectOnce(() => {
    return () => {
      setIsDirty(false);
      setIsValid(true);
    };
  });

  if (isLoading) return <BusinessMetricSelectionSkeleton />;

  return (
    <SHContainer sx={{ px: { xs: "16px", lg: 0 }, py: "25px" }}>
      <StepName
        name={"What does your client value in a business?"}
        stepNumber={stepNumber}
      />
      <BusinessMetricCategories
        label={
          <CategoriesLabel
            icon={<FinancialStrengthSVG />}
            label={"Financial strength"}
          />
        }
        businessMetricData={businessMetricSelections?.financialStrength}
        onChangeBusinessMetric={(financialStrength) => {
          if (businessMetricSelections) {
            dispatch(
              updateBusinessMetricSelectionAction({
                ...businessMetricSelections,
                financialStrength,
              }),
            );
          }
        }}
        setIsDirtyProps={setIsDirty}
      />
      <BusinessMetricCategories
        label={
          <CategoriesLabel icon={<ESGPoliciesSVG />} label={"ESG policies"} />
        }
        businessMetricData={businessMetricSelections?.esg}
        onChangeBusinessMetric={(esg) => {
          if (businessMetricSelections) {
            dispatch(
              updateBusinessMetricSelectionAction({
                ...businessMetricSelections,
                esg,
              }),
            );
          }
        }}
        setIsDirtyProps={setIsDirty}
      />
      <BusinessMetricCategories
        label={
          <CategoriesLabel icon={<OperationalSVG />} label={"Operational"} />
        }
        businessMetricData={businessMetricSelections?.operational}
        onChangeBusinessMetric={(operational) => {
          if (businessMetricSelections) {
            dispatch(
              updateBusinessMetricSelectionAction({
                ...businessMetricSelections,
                operational,
              }),
            );
          }
        }}
        setIsDirtyProps={setIsDirty}
      />
    </SHContainer>
  );
};
